import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaCheck, FaCopy, FaEllipsisH, FaPlus, FaSearch, FaSyncAlt, FaUsers } from 'react-icons/fa'
import { Notify, Confirm } from 'notiflix'
import api from '../../../../helpers/apiRequester'
import shared from '../shared/mhlShared.module.scss'
import styles from './MilitaryHearingLossUsers.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { MhlAgent, MhlTeam } from '../shared/mhlTypes'
import { formatDateTime } from '../shared/mhlFormatting'
import { useMhlAccess } from '../shared/useMhlAccess'
import { useMhlScope } from '../shared/useMhlScope'
import { generateTempPassword, PASSWORD_RULES } from '../shared/passwordUtils'
import { TablePagination, usePagedRows } from '../shared/TablePagination'
import { TeamsModal } from './TeamsModal'
import { colors } from '../../../../ui'

type Role = MhlAgent['role']
type ModalMode = 'create' | 'edit' | null

interface FormState {
    email: string
    name: string
    role: Role
    teamId: number | ''
    tempPassword: string
}

const EMPTY_FORM: FormState = { email: '', name: '', role: 'agent', teamId: '', tempPassword: '' }

const ROLE_LABEL: Record<Role, string> = { admin: 'MHL Admin', team_leader: 'Team leader', agent: 'Agent' }
const ROLE_TONE: Record<Role, 'brand' | 'info' | 'muted'> = { admin: 'brand', team_leader: 'info', agent: 'muted' }
const ROLE_HINT: Record<Role, string> = {
    agent: 'Adds clients and sees those added by themselves and their team-mates. Org: “Military Hearing Loss”.',
    team_leader:
        'An agent who leads the team they belong to: also sees the team’s onboarding stats. Requires a team. Org: “Military Hearing Loss”.',
    admin: 'Reviews and approves clients, manages agents and teams, sees everything. Org: “Military Hearing Loss Admin”.',
}

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback

export const MilitaryHearingLossUsers = () => {
    const { isMhlAdmin, cognitoId: selfId, email: selfEmail } = useMhlAccess()
    const { scope, loading: scopeLoading } = useMhlScope()
    // Team leaders get a read-only view of their own team; admins manage everyone.
    const readOnly = !isMhlAdmin
    const canOpen = isMhlAdmin || !!scope?.isTeamLeader
    const [forbidden, setForbidden] = useState(false)

    const [agents, setAgents] = useState<MhlAgent[]>([])
    const [teams, setTeams] = useState<MhlTeam[]>([])
    const [source, setSource] = useState<'cognito' | 'database' | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState<'all' | Role>('all')
    const [teamFilter, setTeamFilter] = useState<'all' | 'unassigned' | number>('all')
    const [busyKey, setBusyKey] = useState<string | null>(null)

    const [modal, setModal] = useState<ModalMode>(null)
    const [editing, setEditing] = useState<MhlAgent | null>(null)
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [formError, setFormError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [issuedPassword, setIssuedPassword] = useState<{ email: string; password: string; label: string } | null>(
        null,
    )
    const [teamsOpen, setTeamsOpen] = useState(false)

    // Per-row "more actions" menu. Rendered through a portal at a fixed position
    // so the table wrapper's overflow can't clip it.
    const [menu, setMenu] = useState<{ email: string; top: number; right: number } | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)
    const menuFor = menu?.email ?? null
    const openMenu = (email: string, anchor: HTMLElement) => {
        if (menuFor === email) return setMenu(null)
        const r = anchor.getBoundingClientRect()
        setMenu({ email, top: r.bottom + 4, right: window.innerWidth - r.right })
    }
    useEffect(() => {
        if (!menu) return
        const close = () => setMenu(null)
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node
            if (
                menuRef.current &&
                !menuRef.current.contains(t) &&
                !(t instanceof Element && t.closest(`[data-menu-anchor="${menu.email}"]`))
            )
                close()
        }
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', close, true)
        window.addEventListener('resize', close)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
            window.removeEventListener('scroll', close, true)
            window.removeEventListener('resize', close)
        }
    }, [menu])
    const runMenu = (fn: () => void) => {
        setMenu(null)
        fn()
    }

    const isSelf = useCallback(
        (a: MhlAgent) =>
            (!!selfId && a.cognito_id === selfId) || (!!selfEmail && a.email.toLowerCase() === selfEmail.toLowerCase()),
        [selfId, selfEmail],
    )

    const loadTeams = useCallback(async () => {
        try {
            const res = await api({ url: '/military-hearing-loss-teams-list', data: {} })
            if (res.data?.success) setTeams(res.data.teams || [])
        } catch {
            // Teams are optional (migration may not have run yet); page still works.
            setTeams([])
        }
    }, [])

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [res] = await Promise.all([api({ url: '/military-hearing-loss-agents-list', data: {} }), loadTeams()])
            if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load agents')
            setAgents(res.data.users || [])
            setSource(res.data.source || null)
            setForbidden(false)
        } catch (err: any) {
            if (err?.response?.status === 403) {
                setForbidden(true)
                return
            }
            setError(errorMessage(err, 'Failed to load agents'))
        } finally {
            setLoading(false)
        }
    }, [loadTeams])

    useEffect(() => {
        void load()
    }, [load])

    const activeTeams = useMemo(() => teams.filter((t) => Number(t.active) === 1), [teams])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return agents
            .filter((a) => (roleFilter === 'all' ? true : a.role === roleFilter))
            .filter((a) =>
                teamFilter === 'all'
                    ? true
                    : teamFilter === 'unassigned'
                      ? a.team_id == null
                      : a.team_id === teamFilter,
            )
            .filter((a) => (q ? [a.email, a.name, a.team_name].some((v) => (v || '').toLowerCase().includes(q)) : true))
    }, [agents, roleFilter, teamFilter, search])

    const {
        rows: pagedAgents,
        page: agentsPage,
        totalPages: agentsTotalPages,
        setPage: setAgentsPage,
        pageSize: agentsPageSize,
    } = usePagedRows(filtered)

    const counts = useMemo(
        () => ({
            total: agents.length,
            leaders: agents.filter((a) => a.role === 'team_leader').length,
            admins: agents.filter((a) => a.role === 'admin').length,
            active: agents.filter((a) => a.enabled).length,
            disabled: agents.filter((a) => !a.enabled).length,
            unassigned: agents.filter((a) => a.team_id == null).length,
        }),
        [agents],
    )

    // ── Create / edit ──────────────────────────────────────────────────────────

    const openCreate = () => {
        setEditing(null)
        setForm({ ...EMPTY_FORM, tempPassword: generateTempPassword() })
        setFormError(null)
        setModal('create')
    }

    const openEdit = (agent: MhlAgent) => {
        setEditing(agent)
        setForm({
            email: agent.email,
            name: agent.name || '',
            role: agent.role,
            teamId: agent.team_id ?? '',
            tempPassword: '',
        })
        setFormError(null)
        setModal('edit')
    }

    const closeModal = () => {
        if (saving) return
        setModal(null)
        setEditing(null)
        setFormError(null)
    }

    const passwordChecks = useMemo(
        () => PASSWORD_RULES.map((r) => ({ label: r.label, ok: r.test(form.tempPassword) })),
        [form.tempPassword],
    )
    const passwordOk = passwordChecks.every((c) => c.ok)
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email.trim())
    const nameOk = form.name.trim().length > 1
    const leaderNeedsTeam = form.role === 'team_leader' && form.teamId === ''
    const canSubmit = (modal === 'create' ? nameOk && emailOk && passwordOk : nameOk) && !leaderNeedsTeam

    const submitForm = async (e: React.FormEvent) => {
        e.preventDefault()
        setFormError(null)
        if (!nameOk) return setFormError('Enter the agent’s full name.')
        if (modal === 'create' && !emailOk) return setFormError('Enter a valid email address.')
        if (modal === 'create' && !passwordOk) return setFormError('The temporary password does not meet the policy.')
        if (leaderNeedsTeam)
            return setFormError('A team leader must belong to a team — choose the team they will lead.')
        setSaving(true)
        try {
            const teamId = form.teamId === '' ? null : form.teamId
            if (modal === 'create') {
                const email = form.email.trim().toLowerCase()
                const res = await api({
                    url: '/military-hearing-loss-agents-create',
                    data: {
                        email,
                        name: form.name.trim(),
                        role: form.role,
                        temporaryPassword: form.tempPassword,
                        teamId,
                    },
                })
                if (!res.data?.success) throw new Error(res.data?.message || 'Failed to create agent')
                setIssuedPassword({ email, password: form.tempPassword, label: 'Temporary password' })
                Notify.success('Agent created')
            } else if (editing) {
                const res = await api({
                    url: '/military-hearing-loss-agents-update',
                    data: { email: editing.email, name: form.name.trim(), role: form.role, teamId },
                })
                if (!res.data?.success) throw new Error(res.data?.message || 'Failed to update agent')
                Notify.success('Agent updated')
            }
            setModal(null)
            setEditing(null)
            await load()
        } catch (err: any) {
            setFormError(errorMessage(err, 'Something went wrong'))
        } finally {
            setSaving(false)
        }
    }

    // ── Row actions ────────────────────────────────────────────────────────────

    const runAction = async (key: string, fn: () => Promise<void>, failMsg: string) => {
        setBusyKey(key)
        try {
            await fn()
        } catch (err: any) {
            Notify.failure(errorMessage(err, failMsg))
        } finally {
            setBusyKey(null)
        }
    }

    const setTeamInline = (agent: MhlAgent, teamId: number | null) =>
        runAction(
            agent.email,
            async () => {
                const res = await api({
                    url: '/military-hearing-loss-agents-update',
                    data: { email: agent.email, teamId },
                })
                if (!res.data?.success) throw new Error(res.data?.message)
                setAgents((prev) =>
                    prev.map((a) =>
                        a.email === agent.email
                            ? { ...a, team_id: teamId, team_name: teams.find((t) => t.id === teamId)?.name ?? null }
                            : a,
                    ),
                )
                Notify.success(teamId ? 'Team updated' : 'Removed from team')
            },
            'Failed to update team',
        )

    const toggleStatus = (agent: MhlAgent) => {
        const next = !agent.enabled
        Confirm.show(
            next ? 'Enable account' : 'Disable account',
            `${next ? 'Enable' : 'Disable'} ${agent.email}? ${next ? 'They will be able to sign in again.' : 'They will be signed out and unable to sign in.'}`,
            next ? 'Enable' : 'Disable',
            'Cancel',
            () =>
                void runAction(
                    agent.email,
                    async () => {
                        const res = await api({
                            url: '/military-hearing-loss-agents-update-status',
                            data: { email: agent.email, enabled: next },
                        })
                        if (!res.data?.success) throw new Error(res.data?.message)
                        Notify.success(next ? 'Account enabled' : 'Account disabled')
                        await load()
                    },
                    'Failed to update status',
                ),
        )
    }

    const resetPassword = (agent: MhlAgent) => {
        Confirm.show(
            'Reset password',
            `Issue a new temporary password for ${agent.email}? They will be asked to set a new one on next sign-in.`,
            'Reset',
            'Cancel',
            () => {
                const password = generateTempPassword()
                void runAction(
                    agent.email,
                    async () => {
                        const res = await api({
                            url: '/military-hearing-loss-agents-reset-password',
                            data: { email: agent.email, newPassword: password },
                        })
                        if (!res.data?.success) throw new Error(res.data?.message)
                        setIssuedPassword({ email: agent.email, password, label: 'New temporary password' })
                        Notify.success('Password reset')
                    },
                    'Failed to reset password',
                )
            },
        )
    }

    const deleteAgent = (agent: MhlAgent) => {
        Confirm.show(
            'Delete account',
            `Permanently delete ${agent.email} from Cognito and the users table? Their client submissions are kept and stay attributed to them.`,
            'Delete',
            'Cancel',
            () =>
                void runAction(
                    agent.email,
                    async () => {
                        const res = await api({
                            url: '/military-hearing-loss-agents-delete',
                            data: { email: agent.email },
                        })
                        if (!res.data?.success) throw new Error(res.data?.message)
                        Notify.success('Account deleted')
                        await load()
                    },
                    'Failed to delete account',
                ),
        )
    }

    const sync = () =>
        runAction(
            '__sync',
            async () => {
                const res = await api({ url: '/military-hearing-loss-agents-sync', data: {} })
                if (!res.data?.success) throw new Error(res.data?.message)
                const { created = 0, updated = 0, errors = [] } = res.data
                Notify.success(
                    `Synced: ${created} created, ${updated} updated${errors.length ? `, ${errors.length} errors` : ''}`,
                )
                await load()
            },
            'Sync failed',
        )

    const copy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value)
            Notify.success('Copied')
        } catch {
            Notify.failure('Copy failed — select and copy manually')
        }
    }

    if ((!canOpen && !scopeLoading) || forbidden) {
        return (
            <div className={shared.wrap}>
                <div className={shared.emptyState}>
                    <p style={{ margin: 0, fontWeight: 600, color: colors.ink }}>
                        The agents list isn’t available to you
                    </p>
                    <p className={shared.hint} style={{ marginTop: 6 }}>
                        Team leaders can see the agents in their team; MHL admins manage every agent.
                    </p>
                </div>
            </div>
        )
    }

    const editingSelf = modal === 'edit' && !!editing && isSelf(editing)

    return (
        <div className={shared.wrap}>
            <header className={shared.pageHeader}>
                <p className={shared.hint}>
                    {readOnly ? (
                        <>
                            The agents in your team
                            {scope?.teamName ? (
                                <>
                                    , <strong>{scope.teamName}</strong>
                                </>
                            ) : null}
                            . Only MHL admins can add, edit or remove agents — speak to an admin if something needs
                            changing.
                        </>
                    ) : (
                        <>
                            Agents and team leaders for the Military Hearing Loss organisations. Accounts live in
                            Cognito and are mirrored to the dashboard users table, the same as the admin Users page,
                            scoped to MHL. Teams bound what agents can see: an agent sees clients added by their team; a
                            team leader also sees the team’s stats and agents.
                        </>
                    )}
                </p>
                {!readOnly && (
                    <div className={shared.headerActions}>
                        <button type="button" className={shared.btn} onClick={() => setTeamsOpen(true)}>
                            <FaUsers size={11} /> Teams{teams.length ? ` (${activeTeams.length})` : ''}
                        </button>
                        <button
                            type="button"
                            className={shared.btn}
                            onClick={() => void sync()}
                            disabled={busyKey === '__sync'}
                        >
                            <FaSyncAlt size={11} /> {busyKey === '__sync' ? 'Syncing…' : 'Sync from Cognito'}
                        </button>
                        <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={openCreate}>
                            <FaPlus size={11} /> Add agent
                        </button>
                    </div>
                )}
            </header>

            <div className={styles.summaryRow}>
                <span>
                    <strong>{counts.total}</strong> users
                </span>
                {!readOnly && (
                    <span>
                        <strong>{counts.admins}</strong> admins
                    </span>
                )}
                <span>
                    <strong>{counts.leaders}</strong> team leader{counts.leaders === 1 ? '' : 's'}
                </span>
                <span>
                    <strong>{counts.active}</strong> active
                </span>
                {counts.disabled > 0 && (
                    <span>
                        <strong>{counts.disabled}</strong> disabled
                    </span>
                )}
                {!readOnly && (
                    <span>
                        <strong>{activeTeams.length}</strong> teams
                    </span>
                )}
                {!readOnly && counts.unassigned > 0 && (
                    <span>
                        <strong>{counts.unassigned}</strong> not in a team
                    </span>
                )}
                {source === 'database' && (
                    <span className={styles.warnText}>Cognito unavailable — showing the users table mirror</span>
                )}
            </div>

            <div className={shared.toolbar}>
                <div className={shared.searchWrap}>
                    <FaSearch className={shared.searchIcon} />
                    <input
                        className={shared.searchInput}
                        placeholder="Search name, email or team"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className={shared.select}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as 'all' | Role)}
                >
                    <option value="all">All roles</option>
                    <option value="admin">MHL Admins</option>
                    <option value="team_leader">Team leaders</option>
                    <option value="agent">Agents</option>
                </select>
                <select
                    className={shared.select}
                    value={teamFilter === 'all' || teamFilter === 'unassigned' ? teamFilter : String(teamFilter)}
                    onChange={(e) =>
                        setTeamFilter(
                            e.target.value === 'all' || e.target.value === 'unassigned'
                                ? e.target.value
                                : Number(e.target.value),
                        )
                    }
                >
                    <option value="all">All teams</option>
                    <option value="unassigned">Not in a team</option>
                    {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                            {Number(t.active) === 1 ? '' : ' (archived)'}
                        </option>
                    ))}
                </select>
                <span className={shared.resultCount}>
                    {filtered.length} of {agents.length}
                </span>
            </div>

            {error && <div className={shared.errorBanner}>{error}</div>}

            <div className={shared.tableWrap}>
                <table className={`${shared.table} ${styles.agentsTable}`}>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Team</th>
                            <th>Access</th>
                            <th>Status</th>
                            <th>Last sign-in</th>
                            {!readOnly && <th className={styles.actionsCol}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyCell}>
                                    Loading…
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyCell}>
                                    No users match.
                                </td>
                            </tr>
                        ) : (
                            pagedAgents.map((a) => {
                                const self = isSelf(a)
                                const busy = busyKey === a.email
                                const pendingStatus = a.cognito_status && a.cognito_status !== 'CONFIRMED'
                                return (
                                    <tr
                                        key={a.cognito_id || a.email}
                                        className={!a.enabled ? styles.rowDisabled : undefined}
                                    >
                                        <td>
                                            <div className={shared.stackCell}>
                                                <span className={shared.stackPrimary}>
                                                    {a.name || '—'}
                                                    {self && <span className={styles.selfTag}>you</span>}
                                                </span>
                                                {pendingStatus && (
                                                    <span className={shared.stackSecondary}>
                                                        {a.cognito_status!.replace(/_/g, ' ').toLowerCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className={shared.mono}>{a.email}</td>
                                        <td>
                                            <Pill tone={ROLE_TONE[a.role] ?? 'muted'}>
                                                {ROLE_LABEL[a.role] ?? a.role}
                                            </Pill>
                                        </td>
                                        <td>
                                            {readOnly ? (
                                                <span className={shared.stackSecondary}>{a.team_name || '—'}</span>
                                            ) : (
                                                <select
                                                    className={`${shared.select} ${styles.inlineSelect} ${a.team_id == null ? styles.inlineSelectEmpty : ''}`}
                                                    value={a.team_id ?? ''}
                                                    disabled={busy || !a.cognito_id}
                                                    title={
                                                        !a.cognito_id
                                                            ? 'No Cognito id on this user — sync from Cognito first'
                                                            : 'Change team'
                                                    }
                                                    onChange={(e) =>
                                                        void setTeamInline(
                                                            a,
                                                            e.target.value === '' ? null : Number(e.target.value),
                                                        )
                                                    }
                                                >
                                                    <option value="">No team</option>
                                                    {activeTeams.map((t) => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.name}
                                                        </option>
                                                    ))}
                                                    {a.team_id != null &&
                                                        !activeTeams.some((t) => t.id === a.team_id) && (
                                                            <option value={a.team_id}>
                                                                {a.team_name || `Team #${a.team_id}`} (archived)
                                                            </option>
                                                        )}
                                                </select>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className={shared.stackSecondary}
                                                title={
                                                    a.via_membership
                                                        ? `Granted via organisation membership. Primary org: ${a.primary_organisation || '—'}`
                                                        : 'MHL is this user’s primary organisation'
                                                }
                                            >
                                                {a.via_membership
                                                    ? `Membership (${a.primary_organisation || '—'})`
                                                    : 'Primary org'}
                                            </span>
                                        </td>
                                        <td>
                                            <Pill tone={a.enabled ? 'good' : 'bad'} dot>
                                                {a.enabled ? 'Active' : 'Disabled'}
                                            </Pill>
                                        </td>
                                        <td className={shared.stackSecondary}>
                                            {a.last_login ? formatDateTime(a.last_login) : 'Never'}
                                        </td>
                                        {!readOnly && (
                                            <td className={styles.actionsCol}>
                                                <div className={styles.rowActions}>
                                                    <button
                                                        type="button"
                                                        className={`${shared.btn} ${shared.btnSmall}`}
                                                        onClick={() => openEdit(a)}
                                                        disabled={busy}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${shared.btn} ${shared.btnSmall} ${styles.menuButton}`}
                                                        data-menu-anchor={a.email}
                                                        aria-haspopup="menu"
                                                        aria-expanded={menuFor === a.email}
                                                        aria-label="More actions"
                                                        disabled={busy}
                                                        onClick={(e) => openMenu(a.email, e.currentTarget)}
                                                    >
                                                        <FaEllipsisH size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
                <TablePagination
                    page={agentsPage}
                    totalPages={agentsTotalPages}
                    total={filtered.length}
                    pageSize={agentsPageSize}
                    onChange={setAgentsPage}
                />
            </div>

            {menu &&
                (() => {
                    const a = agents.find((x) => x.email === menu.email)
                    if (!a) return null
                    const self = isSelf(a)
                    return createPortal(
                        <div
                            ref={menuRef}
                            className={styles.menu}
                            role="menu"
                            style={{ top: menu.top, right: menu.right }}
                        >
                            <button type="button" role="menuitem" onClick={() => runMenu(() => resetPassword(a))}>
                                Reset password
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                disabled={self}
                                title={self ? "You can't disable your own account" : undefined}
                                onClick={() => runMenu(() => toggleStatus(a))}
                            >
                                {a.enabled ? 'Disable account' : 'Enable account'}
                            </button>
                            <div className={styles.menuDivider} />
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.menuDanger}
                                disabled={self}
                                title={self ? "You can't delete your own account" : undefined}
                                onClick={() => runMenu(() => deleteAgent(a))}
                            >
                                Delete account
                            </button>
                        </div>,
                        document.body,
                    )
                })()}

            {modal && (
                <div className={styles.modalOverlay} onClick={closeModal} role="presentation">
                    <div
                        className={`${styles.modal} ${styles.modalWide}`}
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <div>
                                <h3>{modal === 'create' ? 'Add agent' : 'Edit agent'}</h3>
                                <p className={styles.modalSub}>
                                    {modal === 'create'
                                        ? 'Creates a Cognito account in the Military Hearing Loss organisation and mirrors it to the users table.'
                                        : editing?.email}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={closeModal}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <form className={styles.modalForm} onSubmit={submitForm} noValidate>
                            <div className={styles.modalBody}>
                                <fieldset className={styles.fieldset}>
                                    <legend>Identity</legend>
                                    <div className={styles.fieldGrid}>
                                        <label className={styles.field}>
                                            <span>
                                                Full name <b className={styles.req}>*</b>
                                            </span>
                                            <input
                                                className={shared.searchInput}
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                placeholder="e.g. Sam Taylor"
                                                autoFocus
                                                autoComplete="off"
                                            />
                                        </label>
                                        <label className={styles.field}>
                                            <span>
                                                Email <b className={styles.req}>*</b>
                                            </span>
                                            <input
                                                className={`${shared.searchInput} ${modal === 'edit' ? styles.readOnly : ''}`}
                                                type="email"
                                                value={form.email}
                                                readOnly={modal === 'edit'}
                                                placeholder="name@company.co.uk"
                                                autoComplete="off"
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            />
                                            {modal === 'edit' && (
                                                <small className={styles.fieldHint}>
                                                    The email is the Cognito username and can’t be changed here.
                                                </small>
                                            )}
                                        </label>
                                    </div>
                                </fieldset>

                                <fieldset className={styles.fieldset}>
                                    <legend>Access</legend>
                                    <div className={styles.fieldGrid}>
                                        <div className={styles.field}>
                                            <span>Role</span>
                                            <div className={styles.segmented} role="radiogroup" aria-label="Role">
                                                {(['agent', 'team_leader', 'admin'] as Role[]).map((r) => (
                                                    <button
                                                        key={r}
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={form.role === r}
                                                        className={`${styles.segment} ${form.role === r ? styles.segmentActive : ''}`}
                                                        disabled={editingSelf}
                                                        onClick={() => setForm({ ...form, role: r })}
                                                    >
                                                        {ROLE_LABEL[r]}
                                                    </button>
                                                ))}
                                            </div>
                                            <small className={styles.fieldHint}>
                                                {ROLE_HINT[form.role]}
                                                {editingSelf && ' You can’t change your own role.'}
                                            </small>
                                        </div>
                                        <label className={styles.field}>
                                            <span>
                                                Team {form.role === 'team_leader' && <b className={styles.req}>*</b>}
                                            </span>
                                            <select
                                                className={shared.select}
                                                value={form.teamId === '' ? '' : String(form.teamId)}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        teamId: e.target.value === '' ? '' : Number(e.target.value),
                                                    })
                                                }
                                            >
                                                <option value="">No team</option>
                                                {activeTeams.map((t) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                        {t.member_count ? ` · ${t.member_count}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            <small className={styles.fieldHint}>
                                                {form.role === 'team_leader'
                                                    ? 'The team this agent leads. A leader only ever leads the team they are in.'
                                                    : 'Agents see clients added by themselves and their team-mates; with no team they see only their own. Admins see everything.'}{' '}
                                                {activeTeams.length === 0 && (
                                                    <button
                                                        type="button"
                                                        className={styles.linkButton}
                                                        onClick={() => setTeamsOpen(true)}
                                                    >
                                                        Create the first team
                                                    </button>
                                                )}
                                            </small>
                                        </label>
                                    </div>
                                </fieldset>

                                {modal === 'create' && (
                                    <fieldset className={styles.fieldset}>
                                        <legend>Credentials</legend>
                                        <div className={styles.fieldGrid}>
                                            <label className={styles.field}>
                                                <span>
                                                    Temporary password <b className={styles.req}>*</b>
                                                </span>
                                                <div className={styles.passwordRow}>
                                                    <input
                                                        className={`${shared.searchInput} ${shared.mono}`}
                                                        value={form.tempPassword}
                                                        autoComplete="new-password"
                                                        onChange={(e) =>
                                                            setForm({ ...form, tempPassword: e.target.value })
                                                        }
                                                    />
                                                    <button
                                                        type="button"
                                                        className={`${shared.btn} ${shared.btnSmall}`}
                                                        onClick={() =>
                                                            setForm({ ...form, tempPassword: generateTempPassword() })
                                                        }
                                                    >
                                                        Regenerate
                                                    </button>
                                                </div>
                                                <small className={styles.fieldHint}>
                                                    Shown once after creation. The agent must set their own password on
                                                    first sign-in.
                                                </small>
                                            </label>
                                            <div className={styles.field}>
                                                <span>Policy</span>
                                                <ul className={styles.ruleList}>
                                                    {passwordChecks.map((c) => (
                                                        <li key={c.label} className={c.ok ? styles.ruleOk : ''}>
                                                            <FaCheck size={9} /> {c.label}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </fieldset>
                                )}

                                {formError && <div className={styles.formError}>{formError}</div>}
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={shared.btn} onClick={closeModal} disabled={saving}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className={`${shared.btn} ${shared.btnPrimary}`}
                                    disabled={saving || !canSubmit}
                                >
                                    {saving ? 'Saving…' : modal === 'create' ? 'Create agent' : 'Save changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {issuedPassword && (
                <div className={styles.modalOverlay} onClick={() => setIssuedPassword(null)} role="presentation">
                    <div className={styles.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{issuedPassword.label}</h3>
                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={() => setIssuedPassword(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p className={shared.hint}>
                                Share this with <strong>{issuedPassword.email}</strong> securely. It is shown once and
                                can’t be retrieved later.
                            </p>
                            <div className={styles.passwordRow}>
                                <code className={styles.passwordBox}>{issuedPassword.password}</code>
                                <button
                                    type="button"
                                    className={`${shared.btn} ${shared.btnSmall}`}
                                    onClick={() => void copy(issuedPassword.password)}
                                >
                                    <FaCopy size={11} /> Copy
                                </button>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={`${shared.btn} ${shared.btnPrimary}`}
                                onClick={() => setIssuedPassword(null)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {teamsOpen && (
                <TeamsModal
                    teams={teams}
                    agents={agents}
                    onClose={() => setTeamsOpen(false)}
                    onChanged={async () => {
                        await load()
                    }}
                />
            )}
        </div>
    )
}
